import React from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Box,
  ActionIcon,
  Divider
} from '@mantine/core';
import {
  IconX,
  IconAlertTriangle,
  IconCalendarEvent,
  IconCircleX,
  IconCircleCheck,
  IconInfoCircle
} from '@tabler/icons-react';

interface StatusLegendModalProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Professional Status Legend Modal Component
 * 
 * Displays all 9 possible statuses in the planner grid with professional icons.
 * Positioned as floating modal at bottom-right for hospital-grade UX.
 * 
 * @param opened - Whether the modal is open
 * @param onClose - Function to close the modal
 */
export const StatusLegendModal: React.FC<StatusLegendModalProps> = ({
  opened,
  onClose
}) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconInfoCircle size={20} color="#2563eb" />
          <Text size="lg" fw={600} c="blue">
            Status Legend
          </Text>
        </Group>
      }
      size="md"
      centered={false}
      withCloseButton={false}
      closeOnClickOutside={true}
      closeOnEscape={true}
      trapFocus={false}
      styles={{
        content: {
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e2e8f0',
          maxHeight: '80vh',
          overflow: 'auto'
        },
        header: {
          borderBottom: '1px solid #e2e8f0',
          padding: '16px 20px 12px 20px'
        },
        body: {
          padding: '16px 20px 20px 20px'
        }
      }}
    >
      <Stack gap="md">
        {/* Close Button */}
        <Box style={{ position: 'absolute', top: 16, right: 16 }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={onClose}
            style={{ borderRadius: '50%' }}
          >
            <IconX size={16} />
          </ActionIcon>
        </Box>

        {/* Cell Status Section */}
        <Box>
          <Text size="sm" fw={600} c="dimmed" mb="sm">
            Cell Status
          </Text>
          <Text size="xs" c="dimmed" mb="xs" style={{ fontStyle: 'italic' }}>
            Grid/Calendar view - status for specific time slots
          </Text>
          <Stack gap="xs">
            {/* Available */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconInfoCircle size={12} color="#0ea5e9" />
                </Box>
                <Text size="sm">Available</Text>
              </Group>
              <Text size="xs" c="dimmed">Room ready for assignment</Text>
            </Group>

            {/* Blocked */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#fee2e2',
                    border: '1px solid #ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconCircleX size={12} color="#ef4444" />
                </Box>
                <Text size="sm">Blocked</Text>
              </Group>
              <Text size="xs" c="dimmed">Maintenance or cleaning</Text>
            </Group>

            {/* Inactive */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconCircleX size={12} color="#6b7280" />
                </Box>
                <Text size="sm">Inactive</Text>
              </Group>
              <Text size="xs" c="dimmed">Room disabled or out of service</Text>
            </Group>

            {/* Holiday */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#faf5ff',
                    border: '1px solid #8b5cf6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconCalendarEvent size={12} color="#8b5cf6" />
                </Box>
                <Text size="sm">Holiday</Text>
              </Group>
              <Text size="xs" c="dimmed">Official holiday - no scheduling</Text>
            </Group>

            {/* Conflict */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#fff4e6',
                    border: '1px solid #f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconAlertTriangle size={12} color="#f59e0b" />
                </Box>
                <Text size="sm">Conflict</Text>
              </Group>
              <Text size="xs" c="dimmed">Data integrity warning</Text>
            </Group>
          </Stack>
        </Box>

        <Divider />

        {/* Assignment Status Section */}
        <Box>
          <Text size="sm" fw={600} c="dimmed" mb="sm">
            When Occupied
          </Text>
          <Text size="xs" c="dimmed" mb="xs" style={{ fontStyle: 'italic' }}>
            Assignment status within occupied cells
          </Text>
          <Stack gap="xs">
            {/* Assigned (CONFIRMED) */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#d1fae5',
                    border: '1px solid #10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconCircleCheck size={12} color="#10b981" />
                </Box>
                <Text size="sm">Assigned</Text>
              </Group>
              <Text size="xs" c="dimmed">Confirmed and scheduled</Text>
            </Group>

            {/* Cancelled */}
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconX size={12} color="#6b7280" />
                </Box>
                <Text size="sm">Cancelled</Text>
              </Group>
              <Text size="xs" c="dimmed">Assignment removed</Text>
            </Group>
          </Stack>
        </Box>

        {/* Footer Info */}
        <Box
          style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}
        >
          <Text size="xs" c="dimmed" ta="center">
            Hover over grid cells for detailed information
          </Text>
        </Box>
      </Stack>
    </Modal>
  );
};
